/**
 * Centralized Region Configuration
 * Single source of truth for all country/region-specific settings.
 * All components should import from here instead of doing their own domain detection.
 */

const REGIONS = {
  MN: {
    code: 'MN',
    countryCode: '976',
    flag: '\u{1F1F2}\u{1F1F3}',
    currency: '\u20AE',
    currencyCode: 'MNT',
    phoneLength: 8,
    phonePlaceholder: '8888 8888',
    phoneFormat: (digits) => {
      if (digits.length <= 4) return digits;
      return `${digits.slice(0, 4)} ${digits.slice(4, 8)}`;
    },
    defaultLang: 'mn',
    authMethod: 'otp',
    domain: 'diil.app',
    jobCountryFilter: 'MN', // Filter to show only Mongolian jobs
  },
  UZ: {
    code: 'UZ',
    countryCode: '998',
    flag: '\u{1F1FA}\u{1F1FF}',
    currency: 'UZS',
    currencyCode: 'UZS',
    phoneLength: 9,
    phonePlaceholder: '90 123 45 67',
    phoneFormat: (digits) => {
      if (digits.length <= 2) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      if (digits.length <= 7) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
      return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`;
    },
    defaultLang: 'uz',
    authMethod: 'telegram',
    domain: 'ajob.uz',
    jobCountryFilter: 'UZ',
  },
  KZ: {
    code: 'KZ',
    countryCode: '7',
    flag: '\u{1F1F0}\u{1F1FF}',
    currency: '\u20B8',
    currencyCode: 'KZT',
    phoneLength: 10,
    phonePlaceholder: '701 234 5678',
    phoneFormat: (digits) => {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
    },
    defaultLang: 'ru',
    authMethod: 'telegram',
    domain: 'ajob.kz',
    jobCountryFilter: 'KZ',
  }
};

/**
 * Detect region based on hostname and app context
 * Priority: hostname > Hambi app (locale param / stored session) > default MN
 * @returns {Object} Region config object
 */
export const detectRegion = () => {
  const hostname = window.location.hostname;

  // Hostname-based detection (ajob.uz, ajob.kz)
  if (hostname.includes('ajob.uz')) return REGIONS.UZ;
  if (hostname.includes('ajob.kz')) return REGIONS.KZ;

  // Hambi app detection: locale param in URL means Hambi (UZ)
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('locale')) return REGIONS.UZ;

  // Check stored Hambi session (user returning without locale param)
  try {
    const hambiSession = localStorage.getItem('hambi_session');
    if (hambiSession) return REGIONS.UZ;
  } catch (e) {
    // localStorage not available
  }

  return REGIONS.MN; // Default (diil.app, Toki app)
};

/**
 * Detect region from a phone number prefix
 * @param {string} phone - Full phone number (e.g., "97680201929")
 * @returns {Object} Region config object
 */
export const getRegionByPhone = (phone) => {
  if (!phone) return REGIONS.MN;
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('998')) return REGIONS.UZ;
  if (cleaned.startsWith('7')) return REGIONS.KZ;
  return REGIONS.MN;
};

/**
 * Check if current domain is a Hambi domain (ajob.uz, ajob.kz)
 * Used by TokiContext and HambiContext to correctly attribute URL tokens
 * @returns {boolean}
 */
export const isHambiDomain = () => {
  const hostname = window.location.hostname;
  return hostname.includes('ajob.uz') || hostname.includes('ajob.kz');
};

export { REGIONS };
export default detectRegion;
